import { For, Show, createSignal, onCleanup, type JSX } from 'solid-js';
import { Badge } from '~/components/primitives/badge';
import { Btn } from '~/components/primitives/btn';
import { Readout } from '~/components/primitives/readout';
import { Window } from '~/components/primitives/window';
import { Glyph } from '~/components/glyph';
import { deck } from '~/lib/deck';
import { econLog } from '~/lib/econ-log';
import { economy, HEAT_REDUCTION_CHUNK } from '~/lib/economy';
import { heat } from '~/lib/heat';
import { nodeView } from '~/lib/node-view';
import { player } from '~/lib/player';
import { shopStock } from '~/lib/shop';
import { COUNTER_VERB } from '~/lib/tools';
import { type GameNode, vendorRole } from '~/lib/nodes';

interface VendorModalProps {
  node: GameNode;
  onClose: () => void;
}

/** Shop / service / dual transaction surface. A shop sells a deterministic
 *  subset of the tool catalog; a service reduces heat for ¤. Dual nodes (also
 *  hackable on the MAP) show their current price multiplier or lockout here. */
export function VendorModal(props: VendorModalProps) {
  const node = () => props.node;
  const kind = () => vendorRole(node());

  // Local 1s tick so the decaying price multiplier / lockout countdown read live.
  const [now, setNow] = createSignal(Date.now());
  const id = setInterval(() => setNow(Date.now()), 1000);
  onCleanup(() => clearInterval(id));

  const locked = () => economy.isLockedOut(node().id, now());
  const lockoutSecs = () => Math.ceil(economy.lockoutRemainingMs(node().id, now()) / 1000);
  const multiplier = () => economy.priceMultiplier(node().id, now());
  const surchargePct = () => Math.round((multiplier() - 1) * 100);
  // Viewing is ungated; transacting requires being within ENGAGEMENT_RANGE_M —
  // the SAME shared node-view distance the hack gate uses. Updates reactively
  // as the player (or ghost-walk) moves in/out of range.
  const inRange = () => nodeView.isInRange(node().id);
  // Transacting is blocked when out of range OR locked out (a burned vendor).
  const blocked = () => !inRange() || locked();

  const isDual = () => node().role === 'dual';

  // Buy handlers wrap the economy actions so the DIAG economy log records the
  // ¤ spend (read-only, only on a successful debit). Price/cost are captured
  // before the call since the multiplier is time-varying; the economy action
  // itself is unchanged.
  const buyTool = (tool: Parameters<typeof economy.buyTool>[1]) => {
    const price = economy.priceOf(node().id, tool);
    if (economy.buyTool(node().id, tool, inRange())) {
      econLog.logSpend({ amount: -price, detail: tool.id, nodeId: node().id, isDual: isDual() });
    }
  };
  const buyHeatReduction = () => {
    const cost = economy.heatReductionCost(node().id);
    const cleared = Math.min(HEAT_REDUCTION_CHUNK, heat.value());
    if (economy.buyHeatReduction(node().id, inRange())) {
      econLog.logSpend({
        amount: -cost,
        detail: 'heat_reduction',
        nodeId: node().id,
        isDual: isDual(),
        heatCleared: cleared,
      });
    }
  };

  const containerStyle: JSX.CSSProperties = {
    position: 'absolute',
    inset: 0,
    background: 'var(--sf-overlay)',
    'backdrop-filter': 'blur(4px)',
    display: 'flex',
    'align-items': 'flex-start',
    'justify-content': 'center',
    'padding-top': '28px',
    'padding-bottom': '14px',
    'z-index': 50,
  };
  const windowStyle: JSX.CSSProperties = {
    width: 'calc(100% - 14px)',
    'max-width': '372px',
    color: 'var(--fg)',
    'max-height': 'calc(100% - 14px)',
  };

  return (
    <div style={containerStyle}>
      <Window
        title={kind() === 'shop' ? 'MARKET' : 'SERVICE'}
        dot={node().role === 'dual' ? 'hostile' : 'data'}
        onClose={props.onClose}
        style={windowStyle}
      >
        <div style={{ 'overflow-y': 'auto', flex: 1 }} class="no-scroll">
          {/* Header: node identity + ¤ balance + dual vendor state */}
          <div style={{ padding: '10px 12px', 'border-bottom': '1px solid var(--line-faint)' }}>
            <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
              <span class="t-mono" style={{ 'font-size': '13px', color: 'var(--fg)' }}>
                {node().label}
              </span>
              <Show when={node().role === 'dual'}>
                <Badge tone="warn">DUAL</Badge>
              </Show>
              <span style={{ flex: 1 }} />
              <Readout v={`¤${player.credits().toLocaleString()}`} l="BALANCE" tone="action" />
            </div>
            <div class="t-micro" style={{ 'margin-top': '4px' }}>
              {node().type} · {node().addr}
            </div>
            <Show when={node().role === 'dual' && surchargePct() > 0 && !locked()}>
              <div class="t-micro" style={{ 'margin-top': '6px', color: 'var(--warn)' }}>
                ANNOYED · prices +{surchargePct()}% (cooling down)
              </div>
            </Show>
            <Show when={locked()}>
              <div
                class="panel"
                style={{
                  'margin-top': '6px',
                  padding: '6px 8px',
                  'border-color': 'var(--hostile-line)',
                  background: 'var(--hostile-bg)',
                  display: 'flex',
                  'align-items': 'center',
                  gap: '6px',
                }}
              >
                <Glyph name="lock" size={12} color="var(--hostile)" />
                <span class="t-micro" style={{ color: 'var(--hostile)' }}>
                  LOCKED OUT · vendor offline {lockoutSecs()}s (you burned them)
                </span>
              </div>
            </Show>
            {/* View anywhere; transact only in range (same gate as ENGAGE). */}
            <Show when={!inRange() && !locked()}>
              <div
                class="panel"
                style={{
                  'margin-top': '6px',
                  padding: '6px 8px',
                  'border-color': 'var(--warn-bg)',
                  background: 'var(--warn-bg)',
                  display: 'flex',
                  'align-items': 'center',
                  gap: '6px',
                }}
              >
                <Glyph name="signal" size={12} color="var(--warn)" />
                <span class="t-micro" style={{ color: 'var(--warn)' }}>
                  OUT OF RANGE · move closer to transact (you can still browse)
                </span>
              </div>
            </Show>
          </div>

          {/* SHOP: stocked tools */}
          <Show when={kind() === 'shop'}>
            <div style={{ padding: '10px 12px', display: 'flex', 'flex-direction': 'column', gap: '6px' }}>
              <div class="t-label">STOCK</div>
              <For each={shopStock(node().id)}>
                {(tool) => {
                  const owned = () => deck.isOwned(tool.id);
                  const price = () => economy.priceOf(node().id, tool);
                  const affordable = () => player.canAfford(price());
                  const canBuy = () => !owned() && !blocked() && affordable();
                  return (
                    <div
                      class="panel"
                      style={{ padding: '8px 10px', display: 'flex', 'flex-direction': 'column', gap: '4px' }}
                    >
                      <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
                        <span class="t-mono" style={{ 'font-size': '11px', color: 'var(--fg)' }}>
                          {tool.name}
                        </span>
                        <Badge tone="data">{COUNTER_VERB[tool.counters]}</Badge>
                        <span class="t-micro" style={{ color: 'var(--fg-mute)' }}>STR {tool.strength}</span>
                        <span style={{ flex: 1 }} />
                        <span
                          class="t-mono t-tab"
                          style={{ 'font-size': '11px', color: 'var(--action)' }}
                        >
                          ¤{price().toLocaleString()}
                        </span>
                      </div>
                      <div style={{ display: 'flex', 'align-items': 'center', gap: '8px' }}>
                        <span class="t-micro" style={{ color: 'var(--fg-dim)', flex: 1 }}>
                          {tool.desc}
                        </span>
                        <Show
                          when={!owned()}
                          fallback={<Badge tone="action">OWNED</Badge>}
                        >
                          <Btn
                            variant="primary"
                            disabled={!canBuy()}
                            onClick={() => buyTool(tool)}
                            style={{
                              'justify-content': 'center',
                              padding: '4px 10px',
                              'font-size': '11px',
                              opacity: canBuy() ? 1 : 0.4,
                            }}
                          >
                            {!inRange() ? 'RANGE' : locked() ? 'LOCKED' : affordable() ? 'BUY' : 'NO ¤'}
                          </Btn>
                        </Show>
                      </div>
                    </div>
                  );
                }}
              </For>
            </div>
          </Show>

          {/* SERVICE: heat reduction */}
          <Show when={kind() === 'service'}>
            <div style={{ padding: '10px 12px', display: 'flex', 'flex-direction': 'column', gap: '8px' }}>
              <div class="t-label">REPUTATION MANAGEMENT</div>
              <div
                class="panel-elev"
                style={{ padding: '8px', display: 'flex', 'align-items': 'center', gap: '10px' }}
              >
                <Readout
                  v={String(Math.round(heat.value()))}
                  l="HEAT"
                  tone={heat.value() > 50 ? 'hostile' : heat.value() > 25 ? 'warn' : 'data'}
                />
                <Readout
                  v={`−${Math.min(HEAT_REDUCTION_CHUNK, Math.round(heat.value()))}`}
                  l="CLEARS"
                  tone="action"
                />
                <Readout v={`¤${economy.heatReductionCost(node().id).toLocaleString()}`} l="COST" tone="warn" />
              </div>
              <span class="t-micro" style={{ color: 'var(--fg-dim)' }}>
                Clears a chunk of heat now. The rest you can walk off for free.
              </span>
              <Btn
                variant="primary"
                disabled={blocked() || heat.value() <= 0 || !player.canAfford(economy.heatReductionCost(node().id))}
                onClick={buyHeatReduction}
                style={{ 'justify-content': 'center' }}
              >
                <Glyph name="signal" size={12} /> {inRange() ? 'REDUCE HEAT' : 'OUT OF RANGE'}
              </Btn>
            </div>
          </Show>

          <div style={{ padding: '0 12px 12px' }}>
            <Btn onClick={props.onClose} style={{ width: '100%', 'justify-content': 'center' }}>
              CLOSE
            </Btn>
          </div>
        </div>
      </Window>
    </div>
  );
}
